import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageHero from "../components/PageHero";
import StoryModal from "../components/ri/StoryModal";
import { giv1, activeCampaign, givingStats, charityStories, getStory, fmtDate } from "../data/impactStories";
import { company } from "../data/mock";
import { PoundSterling, HeartHandshake, Mountain, ArrowUpRight, Mail, Sparkles } from "lucide-react";

export default function GivingBack() {
  const [story, setStory] = useState(null);
  const stories = charityStories();

  return (
    <div className="relative" data-testid="giving-back-page">
      <Header />
      <PageHero
        eyebrow="Giving Back"
        title="Building more than"
        accent="infrastructure."
        subtitle="From a pound a week on the payroll to 24 hours on three mountains, giving back is part of how R&C is built."
        image={activeCampaign.image}
        crumbs={[{ label: "Giving Back" }]}
      />

      {/* GIV£1 signature initiative */}
      <section data-testid="giv1-section" className="py-24 lg:py-32 bg-[#08080a]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 reveal">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F5D000] text-black font-heavy text-lg mb-6">
                <PoundSterling className="w-5 h-5" /> GIV£1
              </div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold leading-[1.05] mb-5">
                Our signature scheme, <span className="text-amber-accent">every single month.</span>
              </h2>
              <p className="text-white/70 leading-relaxed mb-6">{giv1.strapline}</p>
              <p className="text-white/60 text-sm leading-relaxed mb-8">
                GIV£1 began when one operative asked how he could personally give to our foodbank pledge.
                Today it is a company-wide scheme that has supported a different local charity every month
                since 2021, and we would love other businesses in our sector to copy it.
              </p>
              <button
                data-testid="giv1-open-story"
                onClick={() => setStory(getStory("giv1-initiative"))}
                className="btn-amber px-6 py-3.5 rounded-md text-sm font-semibold inline-flex items-center gap-2 group"
              >
                Read the GIV£1 story <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </button>
            </div>
            <div className="lg:col-span-7 space-y-4">
              {giv1.how.map((h, i) => (
                <div
                  key={h.step}
                  data-testid={`giv1-step-${h.step}`}
                  className="reveal flex gap-6 p-7 rounded-2xl bg-[#101014] border border-white/8 hover:border-[#F5D000]/40 transition-all"
                  style={{ transitionDelay: `${i * 90}ms` }}
                >
                  <span className="font-heavy text-4xl text-[#F5D000]/30 leading-none">{h.step}</span>
                  <div>
                    <h3 className="font-display text-xl font-bold text-white mb-1.5">{h.title}</h3>
                    <p className="text-sm text-white/60 leading-relaxed">{h.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Live campaign */}
      <section data-testid="campaign-section" className="py-24 lg:py-32 bg-[#0c0c0f]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="reveal rounded-2xl overflow-hidden border border-[#F5D000]/25 grid grid-cols-1 lg:grid-cols-2">
            <div className="relative h-72 lg:h-auto min-h-[320px]">
              <img src={activeCampaign.image} alt={activeCampaign.title} className="w-full h-full object-cover absolute inset-0" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0e0e12]/70 hidden lg:block" />
              <div className="absolute top-5 left-5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F5D000] text-black text-[10px] uppercase tracking-[0.2em] font-bold">
                <Mountain className="w-3.5 h-3.5" /> Live campaign
              </div>
            </div>
            <div className="bg-[#0e0e12] p-8 lg:p-12">
              <div className="text-[11px] uppercase tracking-[0.3em] text-amber-accent mb-3">For {activeCampaign.charity}</div>
              <h2 className="font-display text-3xl lg:text-4xl font-bold leading-tight mb-4">{activeCampaign.title}</h2>
              <p className="text-white/65 leading-relaxed mb-8">
                Ben Nevis, Scafell Pike and Snowdon in 24 hours. The team passed their £2,000 target
                before the first climb and are now pushing towards £3,000 for mental health in construction.
              </p>
              <div className="flex items-end gap-8 mb-8">
                <div>
                  <div className="font-heavy text-4xl text-[#F5D000]">{activeCampaign.raisedLabel.split(" ")[0]}</div>
                  <div className="text-xs text-white/50 mt-1">{activeCampaign.raisedLabel.split(" ").slice(1).join(" ")}</div>
                </div>
                <div>
                  <div className="font-heavy text-4xl text-white">£{activeCampaign.goal.toLocaleString("en-GB")}</div>
                  <div className="text-xs text-white/50 mt-1">stretch goal</div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                {activeCampaign.donateUrl ? (
                  <a href={activeCampaign.donateUrl} target="_blank" rel="noreferrer" data-testid="campaign-donate-btn" className="btn-amber px-7 py-3.5 rounded-md text-sm font-semibold">
                    Support the team
                  </a>
                ) : (
                  <span data-testid="campaign-donate-soon" className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-dashed border-white/25 text-sm text-white/55">
                    <Sparkles className="w-4 h-4 text-[#F5D000]" /> Donation link coming soon
                  </span>
                )}
                <button
                  data-testid="campaign-open-story"
                  onClick={() => setStory(getStory(activeCampaign.storyId))}
                  className="btn-ghost px-6 py-3.5 rounded-md text-sm font-semibold"
                >
                  Read the story
                </button>
              </div>
            </div>
          </div>

          {/* Totals strip */}
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10" data-testid="giving-stats">
            {givingStats.map((s, i) => (
              <div key={s.label} className="reveal bg-[#101014] p-6 lg:p-8" style={{ transitionDelay: `${i * 70}ms` }}>
                <div className="font-heavy text-3xl lg:text-4xl text-[#F5D000] mb-2">{s.value}</div>
                <div className="font-display text-base font-bold text-white leading-snug">{s.label}</div>
                <div className="text-xs text-white/45 mt-1.5">{s.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Causes we've backed */}
      <section data-testid="beneficiaries-section" className="py-24 lg:py-32 bg-[#08080a]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-12 reveal">
            <div className="text-[11px] uppercase tracking-[0.3em] text-amber-accent mb-4">Causes We've Backed</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold leading-[1.05]">
              Nominated by the team, <span className="text-amber-accent">close to home.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {giv1.beneficiaries.map((b, i) => (
              <div
                key={b.name}
                data-testid={`beneficiary-${i}`}
                className="reveal p-7 rounded-2xl bg-[#101014] border border-white/8 hover:border-[#F5D000]/40 transition-all"
                style={{ transitionDelay: `${i * 70}ms` }}
              >
                <div className="w-11 h-11 rounded-xl bg-[#F5D000]/10 border border-[#F5D000]/30 flex items-center justify-center mb-5">
                  <HeartHandshake className="w-5 h-5 text-[#F5D000]" />
                </div>
                <h3 className="font-display text-lg font-bold text-white">{b.name}</h3>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#F5D000] mt-1 mb-3">{b.area}</div>
                <p className="text-sm text-white/60 leading-relaxed">{b.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Charity stories (auto from Impact Stories) */}
      <section data-testid="charity-stories-section" className="py-24 lg:py-32 bg-[#0c0c0f]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12 reveal">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-amber-accent mb-4">From the Impact Wall</div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold leading-[1.05]">
                Community stories, <span className="text-amber-accent">as they happen.</span>
              </h2>
            </div>
            <p className="text-white/55 text-sm max-w-md leading-relaxed">
              This grid updates itself. Every new community or charity Impact Story lands here automatically.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {stories.map((s, i) => (
              <button
                key={s.id}
                data-testid={`charity-story-${s.id}`}
                onClick={() => setStory(s)}
                className="ri-fade-up group text-left rounded-2xl overflow-hidden bg-[#101014] border border-white/8 hover:border-[#F5D000]/50 transition-all"
                style={{ animationDelay: `${(i % 6) * 60}ms` }}
              >
                <div className="relative h-52 overflow-hidden">
                  <img src={s.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute bottom-3.5 left-4 text-[10px] uppercase tracking-[0.2em] text-white/70">{fmtDate(s.date)}</div>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-lg font-bold text-white leading-snug mb-2">{s.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed line-clamp-2">{s.summary}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Suggest a cause */}
      <section data-testid="suggest-cause-section" className="py-24 lg:py-28 bg-[#F5D000] grain">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row lg:items-end justify-between gap-10">
          <div className="max-w-2xl reveal">
            <div className="text-[11px] uppercase tracking-[0.3em] text-black/60 mb-4">Suggest a Cause</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold leading-[1.05] text-black">
              Know a local charity<br />that needs a hand?
            </h2>
            <p className="mt-5 text-black/70 leading-relaxed">
              GIV£1 recipients are nominated by our people, and we're always listening.
              Tell {giv1.contacts[0].name} or {giv1.contacts[1].name} about a cause close to your heart.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 reveal">
            {giv1.contacts.map((c) => (
              <a
                key={c.email}
                href={`mailto:${c.email}?subject=GIV£1 charity nomination`}
                data-testid={`suggest-contact-${c.name.split(" ")[0].toLowerCase()}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md bg-black text-white font-semibold text-sm hover:bg-[#1a1a1e] transition-colors"
              >
                <Mail className="w-4 h-4" /> {c.name}
              </a>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <StoryModal story={story} onClose={() => setStory(null)} onOpenStory={setStory} />
    </div>
  );
}
